import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ChevronRight, CheckCheck, MessageSquare, Loader2, Users, X, Check } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: { label: "Drafting", style: "bg-amber-50 text-amber-700 border-amber-200" },
  submitted: { label: "Submitted", style: "bg-blue-50 text-blue-700 border-blue-200" },
  changes_requested: { label: "Changes requested", style: "bg-rose-50 text-rose-700 border-rose-200" },
  approved: { label: "Approved", style: "bg-violet-50 text-violet-700 border-violet-200" },
  locked: { label: "Locked", style: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function TeamGoals() {
  const { user } = useAuth();
  const [activeCycles, setActiveCycles] = useState([]);
  const [cycle, setCycle] = useState(null);
  const [team, setTeam] = useState([]);
  const [openEmployeeId, setOpenEmployeeId] = useState(null);
  const [openObjectives, setOpenObjectives] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCycles = async () => {
    const r = await api.get("/cycles");
    const active = (r.data.items || []).filter((c) => c.status === "active");
    setActiveCycles(active);
    setCycle(active[0] || null);
  };

  const loadTeam = async () => {
    if (!cycle) return;
    setLoading(true);
    try {
      const r = await api.get("/objectives/team", { params: { cycle_id: cycle.id } });
      setTeam(r.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCycles(); }, []);
  useEffect(() => { if (cycle) loadTeam(); /* eslint-disable-next-line */ }, [cycle?.id]);

  const openEmployee = async (emp) => {
    setOpenEmployeeId(emp.id);
    const r = await api.get("/objectives", { params: { cycle_id: cycle.id, employee_id: emp.id } });
    setOpenObjectives(r.data.items || []);
  };

  const refresh = async () => {
    await loadTeam();
    if (openEmployeeId) {
      const r = await api.get("/objectives", { params: { cycle_id: cycle.id, employee_id: openEmployeeId } });
      setOpenObjectives(r.data.items || []);
    }
  };

  if (!user?.employee_id) {
    return <div className="axia-card p-8 text-sm text-slate-500">Your account isn't linked to an employee record yet.</div>;
  }

  if (!cycle) {
    return (
      <div className="max-w-2xl axia-card p-12 text-center" data-testid="team-goals-no-cycle">
        <Users className="w-7 h-7 mx-auto text-slate-300" />
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 mt-3">No active cycles</h1>
        <p className="text-sm text-slate-500 mt-2">Once HR activates a cycle, you'll see your team's goal-setting progress here.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl space-y-6" data-testid="team-goals-page">
      <div>
        <div className="axia-overline">Manager view</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Team Goals</h1>
        <p className="text-sm text-slate-500 mt-2">Review, approve, or request changes on objectives submitted by your direct reports.</p>
      </div>

      <div className="axia-overline text-slate-500">Cycle: {cycle.name}</div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : team.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500">
          <Users className="w-6 h-6 mx-auto text-slate-300" />
          <div className="font-display text-lg font-semibold text-slate-900 mt-3">No direct reports</div>
        </div>
      ) : (
        <div className="space-y-3">
          {team.map((row) => {
            const meta = STATUS[row.overall_status] || { label: row.overall_status, style: "bg-slate-100 text-slate-600 border-slate-200" };
            return (
              <button
                key={row.employee.id}
                onClick={() => openEmployee(row.employee)}
                className="w-full text-left axia-card p-5 hover:border-slate-400 transition flex items-center gap-4"
                data-testid={`team-row-${row.employee.id}`}
              >
                <div className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
                  {(row.employee.first_name?.[0] || "") + (row.employee.last_name?.[0] || "")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-slate-900">{row.employee.first_name} {row.employee.last_name}</div>
                  <div className="text-xs text-slate-500">{row.employee.designation} · {row.employee.department}</div>
                </div>
                <div className="hidden md:flex items-center gap-6 text-xs">
                  <div>
                    <div className="axia-overline">Objectives</div>
                    <div className="font-mono text-slate-900 font-semibold mt-0.5">{row.objective_count}</div>
                  </div>
                  <div>
                    <div className="axia-overline">Weightage</div>
                    <div className={`font-mono font-semibold mt-0.5 ${row.total_weightage === 100 ? "text-emerald-700" : "text-amber-700"}`}>{row.total_weightage}%</div>
                  </div>
                </div>
                <span className={`axia-pill ${meta.style}`}>{meta.label}</span>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={!!openEmployeeId} onOpenChange={(o) => !o && setOpenEmployeeId(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Review objectives</DialogTitle>
            <DialogDescription>
              Approve, request changes, or sign off as manager.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {openObjectives.length === 0 ? (
              <div className="text-sm text-slate-500">No objectives drafted yet.</div>
            ) : openObjectives.map((o) => (
              <ReviewerObjective key={o.id} objective={o} framework={cycle.framework_snapshot} onChanged={refresh} />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewerObjective({ objective, framework, onChanged }) {
  const [acting, setActing] = useState(false);
  const [reason, setReason] = useState("");
  const fields = framework?.objective_fields || [];

  const decide = async (action) => {
    setActing(true);
    try {
      await api.post(`/objectives/${objective.id}/decision`, { action, reason: reason || null });
      toast.success(action === "approve" ? "Approved" : "Changes requested");
      setReason("");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setActing(false);
    }
  };

  const sign = async () => {
    setActing(true);
    try {
      await api.post(`/objectives/${objective.id}/sign`, { role: "manager" });
      toast.success("Signed off as manager");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign failed");
    } finally {
      setActing(false);
    }
  };

  const meta = STATUS[objective.status] || { label: objective.status, style: "bg-slate-100 text-slate-600 border-slate-200" };

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid={`reviewer-objective-${objective.id}`}>
      <div className="flex items-center gap-2">
        <span className={`axia-pill ${meta.style}`}>{meta.label}</span>
        <span className="axia-overline">weight {objective.weightage}%</span>
        {objective.classification_value && (
          <span className="axia-pill bg-slate-50 text-slate-700 border-slate-200">{objective.classification_value}</span>
        )}
      </div>
      {fields.map((f) => (
        <div key={f.id} className="mt-3">
          <div className="axia-overline">{f.label}</div>
          <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{objective.fields?.[f.id] || <span className="text-slate-400">—</span>}</div>
        </div>
      ))}
      {objective.status === "submitted" && (
        <>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="(Optional) Reason if requesting changes"
            className="mt-4"
            rows={2}
          />
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => decide("request_changes")} disabled={acting} data-testid={`request-changes-${objective.id}`}>
              <X className="w-3.5 h-3.5 mr-1.5" /> Request changes
            </Button>
            <Button size="sm" onClick={() => decide("approve")} disabled={acting} data-testid={`approve-${objective.id}`}>
              <Check className="w-3.5 h-3.5 mr-1.5" /> Approve
            </Button>
          </div>
        </>
      )}
      {objective.status === "approved" && !objective.sign_off?.manager_signed_at && (
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={sign} disabled={acting} data-testid={`manager-sign-${objective.id}`}>
            <CheckCheck className="w-3.5 h-3.5 mr-1.5" /> Sign as manager
          </Button>
        </div>
      )}
      {objective.sign_off?.manager_signed_at && (
        <div className="mt-3 text-xs text-emerald-700 inline-flex items-center gap-1.5"><CheckCheck className="w-3 h-3" /> Manager signed</div>
      )}
    </div>
  );
}
